/* Cali Raised LED cart drawer - 2026-08-06. Paired with crled-cart-drawer.css/.liquid
 *
 * ADD TO CART -> intercepted, posted to /cart/add.js, drawer opens.
 * BUY NOW     -> intercepted too, but after the add we redirect to /checkout.
 *
 * Keeps the header cart count live without editing sections/header.liquid.
 * Keeps Cart.js in sync via CartJS.getCart().
 *
 * 2026-10-21 (Claude): cart drawer fix v2 - line-item keys, protection row last, ordered
 * reads, single settling run, visible failures. See the note above mutSeq.
 */
(function () {
  'use strict';

  var drawer, overlay, body, foot, subEl, hCount, msgEl, busy = false;

  /* 2026-10-21 (Claude, cart drawer fix v2) - WHY THE STATE BELOW EXISTS.
   * Measured on live and draft in isolated test carts: Redo removes and re-adds its
   * protection line (a new price tier) 1-3 s after EVERY cart change. The old drawer read
   * /cart.js once at 1.4 s, so it (a) kept a stale total, (b) drew the protection row late at
   * the TOP, pushing product rows down 113 px, and (c) sent {line: N} - and when the drawer
   * was stale, line N was a DIFFERENT product, so the x removed the wrong item.
   *
   * mutSeq   bumps when ANY cart mutation starts (ours or another script's). A /cart.js read
   *          that was sent before the latest mutation started is out of date and is dropped,
   *          so an older response can never paint over a newer change.
   * readSeq  orders reads; a read older than the one already painted is dropped.
   * ownCall  is true only while WE call window.fetch, so the observer below can tell our
   *          requests from Redo's. We deliberately still go through window.fetch (not a saved
   *          original): Redo watches cart requests to re-tier protection, and bypassing it
   *          would change protection pricing behaviour. */
  var mutSeq = 0, readSeq = 0, paintedRead = 0, ownCall = false, extInFlight = 0;
  var pending = {};            // key -> quantity the shopper asked for while a request was running
  var CART_MUT = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  function ownFetch(url, opts) {
    ownCall = true;
    try { return window.fetch(url, opts); } finally { ownCall = false; }
  }

  function externalStart() { mutSeq++; extInFlight++; }
  var lastOwn = 0, quietT = null;
  function externalEnd() {
    extInFlight = Math.max(0, extInFlight - 1);
    var drawerOpen = drawer && drawer.classList.contains('is-open');
    if (drawerOpen || Date.now() - lastOwn < 15000) { settle(); return; }
    // Drawer closed and the shopper did nothing: one quiet re-read keeps the badge right.
    clearTimeout(quietT);
    quietT = setTimeout(function () { refresh(false); }, 800);
  }

  // Passive: observes the URL only, never changes a request or response.
  (function observe() {
    try {
      var f = window.fetch;
      if (typeof f === 'function' && !f.__crledCd) {
        var wrapped = function (input) {
          var url = typeof input === 'string' ? input : (input && input.url) || '';
          var p = f.apply(this, arguments);
          if (!ownCall && CART_MUT.test(url)) {
            externalStart();
            p.then(externalEnd, externalEnd);
          }
          return p;
        };
        wrapped.__crledCd = true;
        window.fetch = wrapped;
      }
      var XO = XMLHttpRequest.prototype.open, XS = XMLHttpRequest.prototype.send;
      if (!XO.__crledCd) {
        XMLHttpRequest.prototype.open = function (m, u) { this.__crledCdUrl = String(u || ''); return XO.apply(this, arguments); };
        XMLHttpRequest.prototype.open.__crledCd = true;
        XMLHttpRequest.prototype.send = function () {
          if (CART_MUT.test(this.__crledCdUrl || '')) {
            externalStart();
            this.addEventListener('loadend', externalEnd);
          }
          return XS.apply(this, arguments);
        };
      }
    } catch (e) {}
  })();

  /* Visible status line in the drawer footer. Failures used to be swallowed silently. */
  var noteKind = null;   // 'load' (cleared by the next good read) or 'action' (kept until the next action)
  function note(text, kind) {
    if (!msgEl) return;
    msgEl.textContent = text || '';
    msgEl.hidden = !text;
    noteKind = text ? (kind || 'action') : null;
  }
  function clearLoadNote() { if (noteKind === 'load') note(''); }

  /* While a protection re-tier may still be landing, dim the total and mark it busy so a
   * shopper does not read a number that is about to change. */
  function setSettling(on) {
    if (!subEl) return;
    subEl.style.opacity = on ? '0.55' : '';
    subEl.setAttribute('aria-busy', on ? 'true' : 'false');
  }
  var CART_LINK = 'a[href$="/cart"], a[href$="/cart/"]';
  // The theme's own counters: .cart-icon-top in the header, .cart-count on the cart page.
  var THEME_COUNT = '.cart-icon-top, .cart-count, [class*="cart-count"], [class*="cart_count"]';
  // Names a compare_at_price markdown in the summary ("Labor Day Sale  -$50.00").
  // Overwritten from the drawer's [data-cd-sale-label] at init so the label changes
  // per promotion without a code edit. See snippets/crled-cart-drawer.liquid.
  var SALE_LABEL = 'Sale';

  function money(c) { return '$' + (c / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Redo's "Free Returns + Package Protection" line.
   *
   * Identified by Redo's OWN product fields, never a hard-coded variant id: the coverage
   * product carries 96 variants (one per price tier - 3% of cart, min $1 / max $55, plus
   * $2.98 flat return coverage) and Redo picks one per cart, but they all share
   * sku "x-redo", vendor "re:do" and the one handle.
   *
   * It is a real Shopify line item that Redo adds on PAGE LOAD (not at add-to-cart), so
   * it can be absent from a drawer opened straight after ADD TO CART and appear later.
   *
   * We render it WITHOUT a quantity stepper - "package protection x3" is meaningless and
   * was previously reachable - and with a labelled Remove instead. Removal via
   * /cart/change.js sticks across reloads; Redo does not force it back.
   *
   * When Redo's own toggle starts injecting at [data-crled-redo] (blocked until Redo
   * removes us from their coverage experiment), switch this row to hidden and let the
   * toggle represent it - otherwise there will be two competing controls. */
  function isCoverage(i) {
    return i.sku === 'x-redo' || i.vendor === 're:do' ||
      i.handle === 'free-unlimited-return-valid-in-us';
  }

  /* Count of what the shopper actually put in the cart - coverage excluded.
   *
   * Every badge and count is driven from THIS number, not cart.item_count, for three
   * reasons discovered the hard way (2026-08-11):
   *   1. RACE-IMMUNITY. Redo re-tiers its coverage line (remove old variant, add new)
   *      at unpredictable times after any cart mutation. item_count flaps 4->3->4/5
   *      during the swap, and no fixed-delay re-read reliably lands after it - a
   *      settled read still showed 4 while /cart.js later said 5. The REAL-item count
   *      is identical on both sides of a swap, so the badge simply cannot flap.
   *   2. CONSISTENCY. Redo already rewrites counters matching [class*="cart-count"]
   *      site-wide with its own coverage-excluding number (measured: it stomped our
   *      setCount value and even a test value within a tick). Counting the same way
   *      means our surfaces and theirs agree instead of disagreeing by one.
   *   3. SHOPPER MENTAL MODEL. Someone who added 3 products expects "3".
   * snippets/crled-cart-badge-patch.liquid applies the same rule to the label-shaped
   * "Cart (N)" counters, and snippets/top-site-nav.liquid + snippets/mobile-side.liquid
   * server-render the same number - change them together or surfaces disagree. */
  function realCount(cart) {
    var n = 0;
    ((cart && cart.items) || []).forEach(function (i) { if (!isCoverage(i)) n += i.quantity; });
    return n;
  }

  function open() {
    if (!drawer) return;
    overlay.hidden = false;
    // Force reflow so the transition runs. NOT requestAnimationFrame: rAF is throttled
    // or never fires when the page is not actively rendering.
    void overlay.offsetWidth;
    overlay.classList.add('is-open');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('crled-cd-open');
  }

  function close() {
    if (!drawer) return;
    overlay.classList.remove('is-open');
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('crled-cd-open');
    setTimeout(function () { overlay.hidden = true; }, 300);
  }

  /* Give every cart link a LIVE count without adding a second number.
   *
   * The theme already prints <span class="cart-icon-top"> in the header, but it is
   * server-rendered ONLY - verified it does not update on an AJAX cart change, not even
   * after CartJS.getCart(). That was fine before this drawer existed, because adding to
   * cart navigated to /cart and re-rendered the whole page. Now that we keep the shopper
   * in place it goes stale, which is the entire reason a live count is needed here.
   *
   * Creating our own unconditionally put TWO numbers in the header, and they disagreed:
   * the theme's "3" (server-rendered, counting Redo's coverage line) beside our "2"
   * (fetched before Redo had added it). So ADOPT the theme's element where one exists and
   * drive that; only create a badge on links that have no counter of their own (e.g. the
   * mobile menu link, which renders just an icon and the word "Cart").
   *
   * The mobile header icon's bubble (snippets/mobile-side.liquid) is pre-tagged with
   * data-cd-count server-side and its class avoids the "cart-count" substring - see the
   * comment there for why. mountBadges leaves it alone via the first guard.
   *
   * Do not add .crled-cd-count to an adopted element - it should keep the theme's own
   * styling and position. */
  function mountBadges() {
    document.querySelectorAll(CART_LINK).forEach(function (a) {
      if (a.querySelector('[data-cd-count]')) return;
      var existing = a.querySelector(THEME_COUNT);
      if (existing) { existing.setAttribute('data-cd-count', ''); return; }
      if (getComputedStyle(a).position === 'static') a.style.position = 'relative';
      var b = document.createElement('span');
      b.className = 'crled-cd-count';
      b.setAttribute('data-cd-count', '');
      b.hidden = true;
      a.appendChild(b);
    });
  }

  /* ---------------------------------------------------------------------------------
   * /cart.js DISCOUNT FIELDS - read this before touching the maths below.
   *
   * For a CART-LEVEL discount (a code like "10off!", an automatic discount) Shopify does
   * NOT reduce final_line_price. Measured on an $849.99 bed rack with 10off! applied:
   *
   *     original_line_price 84999   final_line_price 84999   <- unchanged!
   *     line_price          76499   total_discount    8500
   *     line_level_discount_allocations  []          <- empty, it is cart-level
   *
   * So `original_line_price > final_line_price` is ALWAYS FALSE for cart-level discounts
   * and is the wrong test. `total_discount` is the reliable per-line signal, populated
   * for both cart-level and line-level discounts.
   * --------------------------------------------------------------------------------- */

  /* Itemised order summary from /cart.js - covers codes, automatic discounts and
   * app/Function discounts identically. The footer used to print total_price alone, so a
   * saved 10% code made an $849.99 bed rack read $765.00 with no explanation. */
  function summary(cart) {
    var origEl = drawer.querySelector('[data-cd-orig]');
    var rowsEl = drawer.querySelector('[data-cd-saverows]');
    var orig = cart.original_total_price != null ? cart.original_total_price : cart.total_price;
    // Markdown savings are invisible to Shopify, so the MSRP subtotal has to be
    // reconstructed. Without this the subtotal and the total print the same number
    // and the summary reads as full price on a cart that is genuinely discounted.
    var saleTotal = ((cart.items) || []).reduce(function (s, i) { return s + saleOffFor(i); }, 0);

    if (origEl) origEl.textContent = money(orig + saleTotal);
    if (subEl) subEl.textContent = money(cart.total_price);

    var noCode = drawer.querySelector('[data-cd-nocode]');
    if (noCode) noCode.hidden = !saleTotal;

    if (!rowsEl) return;

    var byName = {}, order = [];
    function bump(t, amt) {
      t = t || 'Discount';
      if (byName[t] == null) { byName[t] = 0; order.push(t); }
      byName[t] += amt || 0;
    }
    (cart.items || []).forEach(function (i) {
      (i.line_level_discount_allocations || []).forEach(function (a) {
        bump(a.discount_application && a.discount_application.title, a.amount);
      });
    });
    (cart.cart_level_discount_applications || []).forEach(function (d) {
      bump(d.title, d.total_allocated_amount);
    });

    var total = cart.total_discount || 0;
    if (!total && !saleTotal) { rowsEl.innerHTML = ''; rowsEl.hidden = true; return; }

    function row(name, amt) {
      return '<div class="crled-cd__save"><span class="crled-cd__save-name">' + esc(name) +
        '</span><span class="crled-cd__save-amt">&minus;' + money(amt) + '</span></div>';
    }

    var html = '';
    // Markdown row first: it is the headline offer, and it is the one the shopper was
    // promised on the PDP.
    if (saleTotal) html += row(SALE_LABEL, saleTotal);

    // Shopify-side discounts, reconciled against Shopify's own total ONLY. saleTotal is
    // deliberately excluded from this check - it is not part of cart.total_discount, and
    // folding it in would break the reconciliation the original author put here.
    if (total) {
      var named = order.reduce(function (s, t) { return s + byName[t]; }, 0);
      // If named rows do not reconcile with Shopify's total, show one honest "Savings"
      // line rather than a breakdown that does not add up.
      if (order.length && named === total) {
        html += order.map(function (t) { return row(t, byName[t]); }).join('');
      } else {
        html += row('Savings', total);
      }
    }

    rowsEl.innerHTML = html;
    rowsEl.hidden = false;
  }

  /* --- compare_at_price lookup (2026-08-31) ----------------------------------------
   * A price-markdown sale (variant price cut, compare_at_price left at MSRP) produces
   * NO Shopify discount object: total_discount, line_level_discount_allocations and
   * cart_level_discount_applications are all empty. The savings UI above was built for
   * discount CODES and therefore stayed dark on a genuine 10%-off sale - the drawer
   * showed a bare $449.99 on an item the PDP had just shown as $449.99 / was $499.99.
   *
   * /cart.js does NOT return compare_at_price (verified 2026-08-31, all 38 line keys).
   * It does return handle and variant_id, and /products/{handle}.js returns
   * variants[].compare_at_price in CENTS - same unit as /cart.js, so money() applies.
   *
   * Cache the PROMISE, not the result, so concurrent renders share one request. Delete
   * the entry on rejection so a transient failure can retry on the next render rather
   * than disabling savings for the whole session. */
  var compareCache = {};   // handle -> Promise<{ variantId: compareAtCents }>

  function fetchCompare(handle) {
    if (compareCache[handle]) return compareCache[handle];
    var p = fetch('/products/' + encodeURIComponent(handle) + '.js',
        { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
      .then(function (prod) {
        var map = {};
        ((prod && prod.variants) || []).forEach(function (v) {
          if (v.compare_at_price && v.compare_at_price > v.price) map[v.id] = v.compare_at_price;
        });
        return map;
      })
      .catch(function () { delete compareCache[handle]; return {}; });
    compareCache[handle] = p;
    return p;
  }

  /* Markdown saving for one line, in cents. 0 when unknown, not on sale, or coverage.
   * Reads the map stashed on the cached promise so render() can stay synchronous. */
  function saleOffFor(i) {
    if (isCoverage(i)) return 0;
    var c = compareCache[i.handle] && compareCache[i.handle].__map;
    if (!c) return 0;
    var was = c[i.variant_id];
    if (!was || was <= i.price) return 0;
    return (was - i.price) * i.quantity;
  }

  /* Warm the cache for every non-coverage line. Resolves true only if something NEW
   * landed, so refresh() can avoid a pointless second render.
   * MUST be called from refresh() only - never from render(), or the two recurse. */
  function ensureCompare(cart) {
    var handles = [];
    ((cart && cart.items) || []).forEach(function (i) {
      if (!isCoverage(i) && i.handle && handles.indexOf(i.handle) === -1) handles.push(i.handle);
    });
    var fresh = handles.filter(function (h) {
      return !(compareCache[h] && compareCache[h].__map);
    });
    if (!fresh.length) return Promise.resolve(false);
    return Promise.all(fresh.map(function (h) {
      return fetchCompare(h).then(function (map) {
        if (compareCache[h]) compareCache[h].__map = map;   // sync accessor for render()
      });
    })).then(function () { return true; });
  }

  /* renderToken guards render()'s async repaint - see refresh(). It arrived with the
   * "Customers Also Love" upsell row and OUTLIVED it: the compare_at_price repaint added
   * 2026-08-31 depends on it. Do not delete it.
   *
   * 2026-08-31 (Claude): the "Customers Also Love" upsell row was REMOVED, at Jake's
   * request. It ran from 2026-08-11 with no dataLayer event of any kind, so twenty days
   * of it produced no data and none can be recovered; and Shopify's `related` intent was
   * returning wrong-fitment substitutes (a 2016-2023 Tacoma bumper offered against the
   * 2024+ bumper) plus multi-variant "Options" links that navigated the shopper OUT of an
   * open cart with a CHECKOUT button in it.
   *
   * If an upsell returns here: quick-add only, no links out of the drawer, and ship it
   * with tracking on day one. */
  var renderToken = 0;

  // What the drawer is currently showing: total + every line's key and quantity.
  var renderedSig = null;
  function sig(cart) {
    if (!cart || !cart.items) return '';
    return cart.total_price + '|' + cart.items.map(function (i) { return i.key + ':' + i.quantity; }).join(',');
  }

  function render(cart) {
    if (!body) return;
    renderToken++;

    renderedSig = sig(cart);
    if (!cart || !cart.item_count) {
      body.innerHTML = '<p class="crled-cd__empty">Your cart is empty.</p>';
      if (foot) foot.hidden = true;
      setCount(0);
      return;
    }

    // Protection row is rendered LAST, so Redo adding or re-tiering it never moves product rows.
    // Product rows keep Shopify's order.
    var ordered = cart.items.filter(function (i) { return !isCoverage(i); })
      .concat(cart.items.filter(isCoverage));
    body.innerHTML = ordered.map(function (i) {
      // Buttons carry the line item KEY, never a 1-based line number (see the note at the top).
      var line = esc(i.key);
      var cov = isCoverage(i);
      var variant = (i.variant_title && i.variant_title !== 'Default Title' && !cov)
        ? '<p class="crled-cd__var">' + esc(i.variant_title) + '</p>' : '';
      // loading="eager": the drawer sits at translateX(100%), outside the viewport, so
      // lazy images would not load until it slides in - a visible blank flash.
      var img = i.image
        ? '<img class="crled-cd__img" src="' + esc(i.image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2')) +
          '" alt="' + esc(i.product_title) + '" loading="eager" decoding="async" width="76" height="76">'
        : '<div class="crled-cd__img"></div>';

      // See the field notes above: total_discount, not final_line_price.
      // Two independent savings can apply to one line and they stack:
      //   codeOff  - Shopify discount (code / automatic / Function). In total_discount.
      //   saleOff  - compare_at_price markdown. Invisible to Shopify; see saleOffFor().
      // base is the marked-down price x qty, i.e. pre-code but POST-markdown, so the
      // MSRP the shopper should see struck through is base + saleOff.
      var codeOff = i.total_discount || 0;
      var saleOff = saleOffFor(i);
      var base    = (i.original_line_price != null) ? i.original_line_price : i.line_price;
      var wasTot  = base + saleOff;
      var nowTot  = base - codeOff;          // equals i.line_price
      var price = (codeOff + saleOff) > 0
        ? '<span class="crled-cd__price is-disc"><span class="crled-cd__was">' +
          money(wasTot) + '</span>' + money(nowTot) + '</span>'
        : '<span class="crled-cd__price">' + money(i.line_price) + '</span>';

      // Only tag genuine LINE-level promos. A cart-level code is already named once in
      // the summary; repeating it on every row is noise.
      var tags = (i.line_level_discount_allocations || []).map(function (a) {
        var t = (a.discount_application && a.discount_application.title) || 'Discount';
        return '<span class="crled-cd__dtag">' + esc(t) + ' &minus;' + money(a.amount) + '</span>';
      }).join('');

      // Coverage gets a labelled Remove where the stepper would be, and no corner x -
      // one obvious control instead of two, and no meaningless quantity.
      // A quantity the shopper has already asked for (request still queued) wins over the server's.
      var shownQty = pending.hasOwnProperty(i.key) ? pending[i.key] : i.quantity;
      var ctl = cov
        ? '<button type="button" class="crled-cd__covrm" data-cd-rm="' + line + '">Remove</button>'
        : '<span class="crled-cd__qty">' +
            '<button type="button" data-cd-dec="' + line + '" aria-label="Decrease quantity">-</button>' +
            '<span data-cd-qv>' + shownQty + '</span>' +
            '<button type="button" data-cd-inc="' + line + '" aria-label="Increase quantity">+</button>' +
          '</span>';
      var corner = cov ? ''
        : '<button type="button" class="crled-cd__rm" data-cd-rm="' + line + '" aria-label="Remove item">&times;</button>';
      var name = cov
        ? '<span class="crled-cd__name">' + esc(i.product_title) + '</span>' +
          '<p class="crled-cd__var">Optional &middot; added automatically</p>'
        : '<a class="crled-cd__name" href="' + esc(i.url) + '">' + esc(i.product_title) + '</a>';

      return '<div class="crled-cd__item' + (cov ? ' crled-cd__item--cov' : '') + '">' + img +
        '<div class="crled-cd__meta">' + name + variant +
          '<div class="crled-cd__row">' + ctl + price + '</div>' + tags +
        '</div>' + corner +
        '</div>';
    }).join('');

    if (foot) foot.hidden = false;
    summary(cart);
    setCount(realCount(cart));
  }

  function setCount(n) {
    if (hCount) hCount.textContent = n ? ' (' + n + ')' : '';
    document.querySelectorAll('[data-cd-count]').forEach(function (el) {
      el.textContent = n;
      el.hidden = !n;
    });
  }

  function sync() {
    if (window.CartJS && typeof CartJS.getCart === 'function') { try { CartJS.getCart(); } catch (e) {} }
  }

  /* One /cart.js read. Resolves { ok, stale, cart } and never rejects.
   *   ok:false    - network/HTTP/parse failure. Never treated as a stable cart.
   *   stale:true  - a cart mutation started after this read was sent, or a newer read has
   *                 already been painted. Not painted, and not counted as stable. */
  function load() {
    var atMut = mutSeq, id = ++readSeq;
    return ownFetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)); })
      .then(function (cart) {
        if (!cart || !cart.items) throw new Error('bad cart');
        if (id < paintedRead) return { ok: true, stale: true, superseded: true, cart: cart, id: id };
        return { ok: true, stale: atMut !== mutSeq, superseded: false, cart: cart, id: id };
      })
      .catch(function () { return { ok: false, stale: false, cart: null }; });
  }

  function refresh(openAfter) {
    return load().then(function (res) {
        if (!res.ok) {
          note('We couldn\u2019t load your latest cart. Please try again, or open the cart page.', 'load');
          if (openAfter) open();
          return null;
        }
        // Never paint a read older than one already painted. A read overtaken by a newer
        // mutation is painted only when OPENING the drawer (it is still newer than what the
        // drawer holds; settling corrects it moments later).
        if (res.superseded || (res.stale && !openAfter)) { if (openAfter) open(); return res.cart; }
        paintedRead = res.id;
        var cart = res.cart;
        clearLoadNote();
        // Unchanged cart: do not rebuild the rows (a rebuild under the pointer can eat a click).
        if (openAfter || sig(cart) !== renderedSig) render(cart);
        sync();
        if (openAfter) open();
        // Compare-at data needs a network round trip, so the drawer opens immediately
        // with plain prices and repaints once MSRP lands. renderToken guards the
        // repaint: a stale resolve must not overwrite a body render() has since
        // rebuilt for a different cart. Cache is warm after the first open, and the
        // DOMContentLoaded + window.load refreshes usually warm it before any click.
        var tok = renderToken;
        ensureCompare(cart).then(function (changed) {
          if (changed && tok === renderToken) render(cart);
        });
        return cart;
      })
      .catch(function () {});
  }

  /* One delayed re-read after any cart mutation. Redo re-tiers its coverage line
   * (3% of cart across 96 price-tier variants) right AFTER our immediate refresh.
   * The badges are already race-immune (realCount ignores coverage entirely), but the
   * drawer CONTENTS - the coverage row's own price and the estimated total - lag one
   * tier behind until the next read. The timer collapses across rapid mutations so a
   * stepper spree ends with exactly one settling read. */
  /* Settling (v2). ONE run at a time: a new call supersedes the old run (generation check), so
   * runs never overlap. The cart counts as settled only when ALL of these hold:
   *   - two consecutive successful, non-stale reads return the same cart;
   *   - at least SETTLE_MIN_MS have passed since the last change (Redo's re-tier was measured
   *     landing 1.6-3 s after a change, so two quick matching reads at 1.2 s and 2.7 s could
   *     still miss it);
   *   - no cart request from another script (Redo) is in flight.
   * Failed or stale reads reset the count. Any later cart request by another script restarts
   * settling through the observer, so a protection update that lands even later is still shown. */
  var SETTLE_FIRST_MS = 1200, SETTLE_EVERY_MS = 1500, SETTLE_MIN_MS = 4500, SETTLE_MAX_READS = 8;
  var settleGen = 0, settleT = null;
  function settle() {
    var gen = ++settleGen;
    clearTimeout(settleT);
    var t0 = Date.now(), lastSig = null, same = 0, reads = 0;
    setSettling(true);
    function step() {
      if (gen !== settleGen) return;
      reads++;
      load().then(function (res) {
        if (gen !== settleGen) return;
        if (!res.ok || res.stale) { same = 0; lastSig = null; }
        else {
          paintedRead = res.id;
          var s2 = sig(res.cart);
          if (s2 === lastSig) same++; else { same = 1; lastSig = s2; }
          if (s2 !== renderedSig) { render(res.cart); sync(); }
          clearLoadNote();
        }
        var done = same >= 2 && (Date.now() - t0) >= SETTLE_MIN_MS && !extInFlight;
        if (!done && reads < SETTLE_MAX_READS) { settleT = setTimeout(step, SETTLE_EVERY_MS); return; }
        setSettling(false);
        if (!done && (!res.ok)) note('We couldn\u2019t confirm your cart total. Please refresh the page before checking out.', 'load');
      });
    }
    settleT = setTimeout(step, SETTLE_FIRST_MS);
  }

  /* Rapid +/- clicks are coalesced: the drawer waits until the shopper pauses (QTY_IDLE_MS) and
   * sends ONE change with the final quantity. Measured 2026-10-21: sending each click as its own
   * /cart/change.js made Redo's protection re-tier drop the protection line in 2 of 5 runs
   * (0 of 4 on live, which simply ignored the extra clicks). One change per burst is what Redo
   * sees from a single click. */
  var QTY_IDLE_MS = 600, qtyT = null;
  function queueQty(key, qty) {
    pending[key] = qty;
    clearTimeout(qtyT);
    clearTimeout(settleT); settleGen++;    // a read now would only repaint the old quantity
    qtyT = setTimeout(function () {
      if (busy) return;                    // the running request sends pending when it finishes
      var k = Object.keys(pending)[0];
      if (!k) return;
      var q = pending[k]; delete pending[k];
      change(k, q);
    }, QTY_IDLE_MS);
  }

  /* Change one line by KEY. While a request is running, further clicks are queued per key
   * (the shopper's latest wish wins) and sent when it finishes - rapid +/+/+ is not lost. */
  function change(key, qty, opts) {
    opts = opts || {};
    if (busy) { pending[key] = qty; return; }
    var sigBefore = renderedSig;
    busy = true; drawer.classList.add('is-busy');
    lastOwn = Date.now();
    if (noteKind === 'action') note('');   // a new action clears the previous action's message
    var my = ++mutSeq;
    ownFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
      .then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (j) {
          if (!r.ok || !j || !j.items) { var e = new Error((j && (j.description || j.message)) || ('HTTP ' + r.status)); e.status = r.status; throw e; }
          return j;
        });
      })
      .then(function (cart) {
        // Shopify answers 200 with the cart UNCHANGED when the key no longer exists (measured
        // 2026-10-21) - e.g. Redo swapped its protection line a moment earlier. Treat that as a
        // stale key: say so, and for the protection row retry once against its current line.
        var hasKey = cart.items.some(function (i) { return i.key === key; });
        var noop = (qty > 0 && !hasKey) || (qty === 0 && !hasKey && sig(cart) === sigBefore && !opts.retried);
        if (noop) {
          var cov = opts.cov && cart.items.filter(isCoverage)[0];
          if (cov && !opts.retried) { opts.retry = { key: cov.key, qty: qty }; }
          else note('That item changed in your cart a moment ago. Here is your latest cart \u2014 please check it.');
        }
        // Paint only if nothing newer has started and no further click is queued.
        if (my === mutSeq && !Object.keys(pending).length) { render(cart); sync(); }
      })
      .catch(function (e) {
        // 4xx = the line no longer exists as shown (e.g. Redo swapped the protection line) or
        // the quantity is not available. Either way: say so, and show the real cart.
        note(e && e.status >= 400 && e.status < 500
          ? 'Your cart changed before that update. Here is your latest cart \u2014 please check it.'
          : 'We couldn\u2019t update your cart. Here is your latest cart \u2014 please try again.');
        Object.keys(pending).forEach(function (k) { delete pending[k]; });
        renderedSig = null;   // the rows may show an optimistic quantity: force a repaint from the real cart
        return refresh(false);
      })
      .then(function () {
        busy = false; drawer.classList.remove('is-busy');
        if (opts.retry) { var rt = opts.retry; delete opts.retry; change(rt.key, rt.qty, { cov: true, retried: true }); return; }
        var next = Object.keys(pending)[0];
        if (next) { var q = pending[next]; delete pending[next]; change(next, q); return; }
        settle();
      });
  }

  document.addEventListener('DOMContentLoaded', function () {
    drawer = document.querySelector('[data-cd]');
    overlay = document.querySelector('[data-cd-ov]');
    if (!drawer || !overlay) return;

    SALE_LABEL = drawer.getAttribute('data-cd-sale-label') || 'Sale';

    // #shopify-section-footer sets position:relative + z-index:2, creating a stacking
    // context in which our z-index is meaningless. Reparent to <body>.
    if (overlay.parentElement !== document.body) document.body.appendChild(overlay);
    if (drawer.parentElement !== document.body) document.body.appendChild(drawer);

    body = drawer.querySelector('[data-cd-body]');
    foot = drawer.querySelector('[data-cd-foot]');
    subEl = drawer.querySelector('[data-cd-sub]');
    hCount = drawer.querySelector('[data-cd-hcount]');

    // Status line for failures (created here so the snippet's markup does not need to change).
    msgEl = document.createElement('p');
    msgEl.className = 'crled-cd__msg';
    msgEl.setAttribute('role', 'status');
    msgEl.setAttribute('aria-live', 'polite');
    msgEl.hidden = true;
    msgEl.style.cssText = 'margin:0 0 10px;padding:8px 10px;background:#fff4e5;border-left:4px solid #e07b00;color:#3d2600;font-size:13px;line-height:1.35;';
    var subRow = drawer.querySelector('.crled-cd__sub');
    if (subRow && subRow.parentNode) subRow.parentNode.insertBefore(msgEl, subRow);

    mountBadges();
    refresh(false);

    // Redo adds its coverage line on page load, after our first read, so the coverage
    // row and total could settle one short. Re-read once the page has finished loading
    // so the drawer contents agree.
    window.addEventListener('load', function () { refresh(false); });

    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;

      var submitter = e.submitter || document.activeElement;
      var isBuyNow = !!(submitter && submitter.closest && submitter.closest('[data-crled-buy-now]'));

      e.preventDefault();
      if (busy) return;
      busy = true; drawer.classList.add('is-busy');

      mutSeq++; lastOwn = Date.now();
      ownFetch('/cart/add.js', { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
        .then(function () {
          // 2026-09-30 (Claude): BUY NOW used to go straight to /checkout, which skipped
          // Gift Box - it only runs from the CHECKOUT control it clones into the drawer
          // (.giftbox-checkout-cloned). Open the drawer, give Gift Box a beat to read
          // the new cart, then press ITS button. Restore the old one-liner after the
          // promo if wanted: if (isBuyNow) { window.location.assign('/checkout'); return; }
          if (isBuyNow) {
            return refresh(true).then(function () {
              settle();
              setTimeout(function () {
                var co = drawer.querySelector('.giftbox-checkout-cloned') ||
                  drawer.querySelector('[data-cd-checkout]');
                if (co) co.click(); else window.location.assign('/checkout');
              }, 1500);
            });
          }
          return refresh(true).then(function () { settle(); });
        })
        .catch(function () { form.submit(); })
        .finally(function () { busy = false; drawer.classList.remove('is-busy'); });
    }, true);

    document.addEventListener('click', function (e) {
      // 2026-08-10 (Claude): never swallow a click the browser is meant to handle itself.
      // cmd/ctrl/shift/alt-click and any non-primary button now reach the browser, so
      // "open in new tab / new window" works on every link in and around the drawer.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      // 2026-08-10 (Claude): the branch that used to stand here was
      //     var link = e.target.closest(CART_LINK);
      //     if (link) { e.preventDefault(); refresh(true); return; }
      // It preventDefault()ed EVERY <a href="*/cart"> on the site, so /cart was
      // unreachable - including from the header. At the time that also orphaned the
      // required attributes[location-type] select that only templates/cart.liquid
      // rendered, so cart links were switched to navigate normally.
      //
      // 2026-08-10 later (Claude): that select is now GONE - removed at Jake's request
      // together with cart.s.min.js's #checkout blocker - so the constraint above no
      // longer applies. Jake chose drawer-over-navigation for the HEADER ICONS ONLY.
      // 2026-08-11: desktop parity - the desktop header cart icon (li.cart-bg > a in
      // snippets/top-site-nav.liquid) now opens the drawer like the mobile icon does.
      // Every other cart link (mobile menu footer, cart page) still navigates, so
      // /cart stays reachable for its lead-time badges and shipping notes. Both icons
      // keep href="/cart" as the no-JS and modifier-click fallback, and the guard
      // above keeps cmd/ctrl-click native.
      var hdrCart = e.target.closest('.mobile-menu__cart, li.cart-bg > a[href$="/cart"]');
      if (hdrCart) { e.preventDefault(); refresh(true); return; }

      if (e.target.closest('[data-cd-close]') || e.target.closest('[data-cd-ov]')) { close(); return; }
      var inc = e.target.closest('[data-cd-inc]');
      var dec = e.target.closest('[data-cd-dec]');
      var rm  = e.target.closest('[data-cd-rm]');
      if (inc || dec || rm) {
        var el = inc || dec || rm;
        var key = el.getAttribute('data-cd-inc') || el.getAttribute('data-cd-dec') || el.getAttribute('data-cd-rm');
        if (!key) return;
        if (rm) return change(key, 0, { cov: rm.classList.contains('crled-cd__covrm') });
        var wrap = el.closest('.crled-cd__qty');
        var qv = wrap ? wrap.querySelector('[data-cd-qv]') : null;
        var cur = qv ? parseInt(qv.textContent, 10) : 1;
        var nq = inc ? cur + 1 : Math.max(0, cur - 1);
        if (qv) qv.textContent = nq;   // immediate feedback; the server's answer replaces it
        return queueQty(key, nq);
      }
    });

    document.addEventListener('keyup', function (e) { if (e.key === 'Escape') close(); });
  });
})();
